"use client";

import { useCallback, useMemo, useState } from "react";
import { evaluateResumeEligibility, isAppcastJob } from "@artha/utils";
import { isAppcastResumeEligibilityEnabled } from "../personalization.constants";
import type { UseResumeEligibilityApplyInput } from "../personalization.types";

export function useResumeEligibilityApply({
  job,
  trackAndRedirect,
  onRedirect,
  creatorView = false,
}: UseResumeEligibilityApplyInput) {
  const [isOpen, setIsOpen] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const eligibility = useMemo(
    () =>
      evaluateResumeEligibility({
        country: job?.country,
        cpa_value: job?.cpa_value,
        rph_value: job?.rph_value,
        work_mode: job?.work_mode,
        aggregator_name: job?.aggregator_name,
      }),
    [
      job?.country,
      job?.cpa_value,
      job?.rph_value,
      job?.work_mode,
      job?.aggregator_name,
    ],
  );
  const shouldVerify = Boolean(
    job &&
    !creatorView &&
    eligibility.eligible &&
    (!isAppcastJob(job) || isAppcastResumeEligibilityEnabled),
  );
  const skipFitCheck = eligibility.gate === "collect_only";

  const apply = useCallback(async () => {
    if (!job) return;
    if (shouldVerify) {
      setIsOpen(true);
      return;
    }
    await trackAndRedirect(job, onRedirect);
  }, [job, onRedirect, shouldVerify, trackAndRedirect]);

  const complete = useCallback(async () => {
    if (!job || isRedirecting) return;
    setIsRedirecting(true);
    try {
      await trackAndRedirect(job, onRedirect);
    } finally {
      setIsRedirecting(false);
    }
  }, [isRedirecting, job, onRedirect, trackAndRedirect]);

  return {
    eligibility,
    shouldVerify,
    skipFitCheck,
    isOpen,
    isRedirecting,
    setIsOpen,
    apply,
    complete,
  };
}
